import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";

const TestDataContext = createContext(null);

const todayISO = () => new Date().toISOString().split("T")[0];

// Shorthand: a per-language target cell. `de: null` means "skip in DE Excel".
const loc = (fr, de) => ({ fr, de });
// Shorthand: bilingual label.
const L = (fr, de) => ({ fr, de });

export const defaultTests = {
  "TMT A (CERAD, 2005 / GREFEX, 2008)": {
    label: L("TMT A (CERAD, 2005 / GREFEX, 2008)", "TMT A (CERAD, 2005 / GREFEX, 2008)"),
    params: [
      { name: "tempsRaw", label: L('Temps "', 'Zeit "'), type: "input", value: "", excludeFromResult: true },
      { name: "erreurs", label: L("Erreurs", "Fehler"), type: "tmtAErreursRaw", value: "" },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "Trail_A",
    resultLoc: loc("G14", "G14"),
  },
  "TMT B (CERAD, 2005 / GREFEX, 2008)": {
    label: L("TMT B (CERAD, 2005 / GREFEX, 2008)", "TMT B (CERAD, 2005 / GREFEX, 2008)"),
    params: [
      { name: "tempsRaw", label: L('Temps "', 'Zeit "'), type: "input", value: "", excludeFromResult: true },
      { name: "erreursPC", label: L("Erreurs PC", "Fehler PR"), type: "input", value: "", italic: true },
      { name: "perseverations", label: L("Persévérations", "Perseverationen"), type: "tmtBPersevRaw", value: "", italic: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "Trail_B",
    resultLoc: loc("G29", "G26"),
  },
  "Color Trail 1": {
    label: L("Color Trail 1", "CTT I"),
    params: [
      { name: "temps", label: L('temps "', 'Zeit "'), type: "input", value: "" },
      { name: "erreurs", label: L("erreurs", "Fehler"), type: "input", value: "" },
    ],
    type: "parquet",
    trailNumber: 1,
    resultLoc: loc("G15", "G15"),
  },
  "Color Trail 2": {
    label: L("Color Trail 2", "CTT II"),
    params: [
      { name: "temps", label: L('temps "', 'Zeit "'), type: "input", value: "" },
      { name: "erreurs", label: L("erreurs", "Fehler"), type: "input", value: "" },
    ],
    type: "parquet",
    trailNumber: 2,
    resultLoc: loc("G30", "G27"),
  },
  "TAP-Alerte": {
    label: L("TAP-Alerte", "Alertness (TAP)"),
    params: [
      { name: "toniqueTRPC", label: L("tonique TR PC", "tonisch RZ PR"), type: "input", value: "" },
      { name: "sDPC", label: L("SD PC", "SD PR"), type: "input", value: "" },
      { name: "phasiqueTRPC", label: L("phasique TR PC", "phasisch RZ PR"), type: "input", value: "" },
      { name: "sDPC2", label: L("SD PC", "SD PR"), type: "input", value: "" },
      { name: "indiceDAlertePhasiquePC", label: L("Indice d'alerte phasique PC", "Kennwert Alertness PR"), type: "input", value: "" },
      { name: "nombreDAnticipations", label: L("Nombre d'anticipations", "Antizipationen"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G7", "G7"),
  },
  "TAP-GoNogo": {
    label: L("TAP-GoNogo", "Go/Nogo (TAP)"),
    params: [
      { name: "tRPC", label: L("TR PC", "RZ PR"), type: "input", value: "" },
      { name: "fautesPC", label: L("Fautes PC", "Fehler PR"), type: "input", value: "" },
      { name: "omissionsPC", label: L("Omissions PC", "Auslassungen PR"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G8", "G8"),
  },
  "TAP-AttentionDivisée": {
    label: L("TAP-AttentionDivisée", "Geteilte Aufmerksamkeit (TAP)"),
    params: [
      { name: "omissionsAuditivesPC", label: L("Omissions auditives PC", "Ausl. auditiv PR"), type: "input", value: "" },
      { name: "omissionsVisuellesPC", label: L("Omissions visuelles PC", "Ausl. visuell PR"), type: "input", value: "" },
      { name: "omissionsTotalesPC", label: L("Omissions totales PC", "Ausl. gesamt PR"), type: "input", value: "" },
      { name: "fautesPC", label: L("Fautes PC", "Fehler PR"), type: "input", value: "" },
      { name: "tRAuditifsPC", label: L("TR auditifs PC", "RZ auditiv PR"), type: "input", value: "" },
      { name: "tRVisuelsPC", label: L("TR visuels PC", "RZ visuell PR"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G9", "G9"),
  },
  "TAP-M-Alerte": {
    label: L("TAP-M-Alerte", "Alertness (TAP-M)"),
    params: [
      { name: "tRPC", label: L("TR PC", "RZ PR"), type: "input", value: "" },
      { name: "sDPC", label: L("SD PC", "SD PR"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G10", "G10"),
  },
  "TAP-M-GoNogo": {
    label: L("TAP-M-GoNogo", "Go/Nogo (TAP-M)"),
    params: [
      { name: "tRPC", label: L("TR PC", "RZ PR"), type: "input", value: "" },
      { name: "fautesPC", label: L("Fautes PC", "Fehler PR"), type: "input", value: "" },
      { name: "omissionsPC", label: L("Omissions PC", "Auslassungen PR"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G11", "G11"),
  },
  "TAP-M-Distractibilité": {
    label: L("TAP-M-Distractibilité", "Ablenkbarkeit (TAP-M)"),
    params: [
      { name: "avecDistracteursTRPC", label: L("Avec distracteurs : TR PC", "Mit Ablenkern: RZ PR"), type: "input", value: "" },
      { name: "erreursPC", label: L("Erreurs PC", "Fehler PR"), type: "input", value: "" },
      { name: "omissionsPC", label: L("Omissions PC", "Auslassungen PR"), type: "input", value: "" },
      { name: "sansDistracteurTRPC", label: L("Sans distracteur : TR PC", "Ohne Ablenker: RZ PR"), type: "input", value: "" },
      { name: "erreursPC2", label: L("Erreurs PC", "Fehler PR"), type: "input", value: "" },
      { name: "omissionsPC2", label: L("Omissions PC", "Auslassungen PR"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G12", "G13"),
  },
  "TAP-M-AttentionDivisée": {
    label: L("TAP-M-AttentionDivisée", "Geteilte Aufmerksamkeit (TAP-M)"),
    params: [
      { name: "omissionsAuditivesPC", label: L("Omissions auditives PC", "Ausl. auditiv PR"), type: "input", value: "" },
      { name: "omissionsVisuellesPC", label: L("Omissions visuelles PC", "Ausl. visuell PR"), type: "input", value: "" },
      { name: "omissionsTotalesPC", label: L("Omissions totales PC", "Ausl. gesamt PR"), type: "input", value: "" },
      { name: "fautesPC", label: L("Fautes PC", "Fehler PR"), type: "input", value: "" },
      { name: "tRAuditifsPC", label: L("TR auditifs PC", "RZ auditiv PR"), type: "input", value: "" },
      { name: "tRVisuelsPC", label: L("TR visuels PC", "RZ visuell PR"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G13", "G12"),
  },
  "TAP-ChampVisuel": {
    label: L("TAP-ChampVisuel", "Gesichtsfeld (TAP)"),
    params: [
      { name: "tRGauchePC", label: L("TR gauche PC", "RZ links PR"), type: "input", value: "" },
      { name: "tRDroitePC", label: L("TR droite PC", "RZ rechts PR"), type: "input", value: "" },
      { name: "diffTrGd", label: L("Diff. TR G-D", "Diff. RZ L-R"), type: "significatifToggle", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G81", "G73"),
  },
  "TAP-Négligence": {
    label: L("TAP-Négligence", "Neglect (TAP)"),
    params: [
      { name: "tRGauchePC", label: L("TR gauche PC", "RZ links PR"), type: "input", value: "" },
      { name: "tRDroitePC", label: L("TR droite PC", "RZ rechts PR"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G82", "G74"),
  },
  "TAP-BalayageVisuel": {
    label: L("TAP-BalayageVisuel", "Visuelles Scanning (TAP)"),
    params: [
      { name: "tRNonCritPC", label: L("TR non crit. PC", "RZ n. krit. PR"), type: "input", value: "" },
      { name: "fautesPC", label: L("Fautes PC", "Fehler PR"), type: "input", value: "" },
      { name: "omissionsPC", label: L("Omissions PC", "Auslassungen PR"), type: "input", value: "" },
      { name: "omissionsColonnes12", label: L("Omissions colonnes 1+2", "Ausl. Spalten 1+2"), type: "input", value: "" },
      { name: "colonnes45", label: L("colonnes 4+5", "Spalten 4+5"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G83", "G75"),
  },
  "TAP-M-ChampVisuelActif": {
    label: L("TAP-M-ChampVisuelActif", "Aktives Gesichtsfeld (TAP-M)"),
    params: [
      { name: "tRGauchePC", label: L("TR gauche PC", "RZ links PR"), type: "input", value: "" },
      { name: "tRDroitePC", label: L("TR droite PC", "RZ rechts PR"), type: "input", value: "" },
      { name: "omissionsPC", label: L("Omissions PC", "Auslassungen PR"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G84", null),
  },
  "TAP-M-BalayageVisuel": {
    label: L("TAP-M-BalayageVisuel", "Visuelles Scanning (TAP-M)"),
    params: [
      { name: "tRNonCritPC", label: L("TR non crit. PC", "RZ n. krit. PR"), type: "input", value: "" },
      { name: "fautesPC", label: L("Fautes PC", "Fehler PR"), type: "input", value: "" },
      { name: "omissionsPC", label: L("Omissions PC", "Auslassungen PR"), type: "input", value: "" },
      { name: "omissionsColonnes12", label: L("Omissions colonnes 1+2", "Ausl. Spalten 1+2"), type: "input", value: "" },
      { name: "colonnes45", label: L("colonnes 4+5", "Spalten 4+5"), type: "input", value: "" },
      { name: "commentaire", label: L("commentaire", "Kommentar"), type: "input", value: "" },
    ],
    type: "rtf",
    calcFunction: "ref vers parser RTF",
    resultLoc: loc("G85", "G76"),
  },
  "Code (WAIS-IV)": {
    label: L("Code (WAIS-IV)", "Zahlen-Symbol-Test (WAIS-IV)"),
    params: [
      { name: "rawScore", label: L("Score brut (Corrects - Erreurs)", "Rohwert (Korrekt - Fehler)"), type: "input", value: "" },
    ],
    type: "parquet",
    calcType: "waisCode",
    resultLoc: loc("G18", "G17"),
  },
  "Code (WAIS-III)": {
    label: L("Code (WAIS-III)", "Zahlen-Symbol-Test (WAIS-III)"),
    params: [
      { name: "rawScore", label: L("Score brut (Corrects - Erreurs)", "Rohwert (Korrekt - Fehler)"), type: "input", value: "" },
    ],
    type: "parquet",
    calcType: "waisCodeIII",
    resultLoc: loc("G19", null),
  },
  "Fluences Animaux (CERAD, 2005)": {
    label: L("Fluences Animaux (CERAD, 2005)", "Fluences Animaux (CERAD, 2005)"),
    params: [
      { name: "total", label: L('Total 60"', 'Total 60"'), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "SemFl",
    resultLoc: loc("G28", "G28"),
  },
  "Fluences Mots M (CERAD, 2005)": {
    label: L("Fluences Mots M (CERAD, 2005)", "Fluences Mots M (CERAD, 2005)"),
    params: [
      { name: "total", label: L('Total 60"', 'Total 60"'), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "SWorter",
    resultLoc: loc("G27", "G27"),
  },
  "10 mots (CERAD, 2005)": {
    label: L("10 mots — Apprentissage (CERAD, 2005)", "10 mots — Apprentissage (CERAD, 2005)"),
    params: [
      { name: "r1", label: L("Essai 1", "Durchgang 1"), type: "input", value: "", excludeFromResult: true },
      { name: "r2", label: L("Essai 2", "Durchgang 2"), type: "input", value: "", excludeFromResult: true },
      { name: "r3", label: L("Essai 3", "Durchgang 3"), type: "input", value: "", excludeFromResult: true },
      { name: "i1", label: L("Intrusions 1", "Intrusionen 1"), type: "input", value: "", excludeFromResult: true },
      { name: "i2", label: L("Intrusions 2", "Intrusionen 2"), type: "input", value: "", excludeFromResult: true },
      { name: "i3", label: L("Intrusions 3", "Intrusionen 3"), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "WL_Lern",
    resultLoc: loc("G45", "G45"),
  },
  "10 mots (CERAD, 2005) (Row 48)": {
    label: L("10 mots — Rappel (CERAD, 2005)", "10 mots — Rappel (CERAD, 2005)"),
    params: [
      { name: "richtige", label: L("Mots rappelés", "Erinnerte Wörter"), type: "input", value: "", excludeFromResult: true },
      { name: "intrusions", label: L("Intrusions", "Intrusionen"), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "WL_Abruf",
    resultLoc: loc("G46", "G46"),
  },
  "10 mots (CERAD, 2005) (Row 49)": {
    label: L("10 mots — Reconnaissance (CERAD, 2005)", "10 mots — Wiedererkennen (CERAD, 2005)"),
    params: [
      { name: "richtigeJa", label: L("Cibles reconnues", "Erkannte Zielwörter"), type: "input", value: "", excludeFromResult: true },
      { name: "richtigeNein", label: L("Distracteurs rejetés", "Abgelehnte Distraktoren"), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "Diskri",
    resultLoc: loc("G47", "G47"),
  },
  "Copie de figures (CERAD, 2005)": {
    label: L("Copie de figures (CERAD, 2005)", "Figuren abzeichnen (CERAD, 2005)"),
    params: [
      { name: "kreis", label: L("Cercle", "Kreis"), type: "input", value: "", excludeFromResult: true },
      { name: "rhombus", label: L("Losange", "Rhombus"), type: "input", value: "", excludeFromResult: true },
      { name: "rechtecke", label: L("Rectangles", "Rechtecke"), type: "input", value: "", excludeFromResult: true },
      { name: "wuerfel", label: L("Cube", "Würfel"), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "Fig_abz",
    resultLoc: loc("G86", "G86"),
  },
  "Rappel de figures (CERAD, 2005)": {
    label: L("Rappel de figures (CERAD, 2005)", "Figuren erinnern (CERAD, 2005)"),
    params: [
      { name: "item1", label: L("Cercle", "Kreis"), type: "input", value: "", excludeFromResult: true },
      { name: "item2", label: L("Losange", "Rhombus"), type: "input", value: "", excludeFromResult: true },
      { name: "item3", label: L("Rectangles", "Rechtecke"), type: "input", value: "", excludeFromResult: true },
      { name: "item4", label: L("Cube", "Würfel"), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "Fig_abr",
    resultLoc: loc("G73", "G73"),
  },
  "Boston Naming Test (CERAD, 2005)": {
    label: L("Boston Naming Test (CERAD, 2005)", "Boston Naming Test (CERAD, 2005)"),
    params: [
      { name: "total", label: L("Total (/15)", "Total (/15)"), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    ceradScale: "Boston",
    resultLoc: loc("G108", "G108"),
  },
  "MMSE (CERAD, 2005)": {
    label: L("MMSE (CERAD, 2005)", "MMSE (CERAD, 2005)"),
    params: [
      { name: "total", label: L("Total (/30)", "Total (/30)"), type: "input", value: "", excludeFromResult: true },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "Perte de points à" },
    ],
    type: "manual",
    ceradScale: "MMS",
    resultLoc: loc("G117", "G117"),
  },

  // --- Manual entry tests ---
  "Symboles (WAIS-IV)": {
    label: L("Symboles (WAIS-IV)", "Symbol-Suche (WAIS-IV)"),
    params: [{ name: "rawScore", label: L("Score brut (Corrects - Erreurs)", "Rohwert (Korrekt - Fehler)"), type: "input", value: "" }],
    type: "parquet",
    calcType: "waisSym",
    resultLoc: loc("G16", "G16"),
  },
  "Symboles (WAIS-III)": {
    label: L("Symboles (WAIS-III)", "Symbol-Suche (WAIS-III)"),
    params: [{ name: "rawScore", label: L("Score brut (Corrects - Erreurs)", "Rohwert (Korrekt - Fehler)"), type: "input", value: "" }],
    type: "parquet",
    calcType: "waisSymIII",
    resultLoc: loc("G17", null),
  },
  "Mémoire des chiffres (WAIS-IV)": {
    label: L("Mémoire des chiffres (WAIS-IV)", "Zahlenspanne (WAIS-IV)"),
    params: [
      { name: "empanInverseRaw", label: L("Empan inverse", "Spanne rückwärts"), type: "empanRaw", kind: "emci", value: "" },
      { name: "empanInverseStability", type: "stability", linkedTo: "empanInverseRaw", value: "" },
      { name: "mciRaw", label: L("MCI brut", "MCI Rohwert"), type: "subtestRaw", kind: "mci", nsLabel: L("MCI NS", "MCI WP"), value: "" },
      { name: "empanCroissantRaw", label: L("Empan croissant", "Sequentielle Spanne"), type: "empanRaw", kind: "emcc", value: "" },
      { name: "empanCroissantStability", type: "stability", linkedTo: "empanCroissantRaw", value: "" },
      { name: "mccRaw", label: L("MCC brut", "MCC Rohwert"), type: "subtestRaw", kind: "mcc", nsLabel: L("MCC NS", "MCC WP"), value: "" },
    ],
    type: "manual",
    resultLoc: loc("G21", "G19"),
  },
  "Mémoire des chiffres (WAIS-III)": {
    label: L("Mémoire des chiffres (WAIS-III)", "Zahlenspanne (WAIS-III)"),
    params: [
      { name: "empanInverseRaw", label: L("Empan inverse", "Spanne rückwärts"), type: "empanRaw", kind: "emci", value: "" },
      { name: "empanInverseStability", type: "stability", linkedTo: "empanInverseRaw", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G22", null),
  },
  "Empan inverse (WMS-R)": {
    label: L("Empan inverse (WMS-R)", "Blockspanne rückwärts (WMS-R)"),
    params: [
      { name: "empanRaw", label: L("Score d'empan", "Spannenwert"), type: "input", value: "" },
      { name: "empanStability", type: "stability", linkedTo: "empanRaw", value: "" },
      { name: "scoreGlobal", label: L("Score global", "Gesamtrohwert"), type: "wmsrBlockspanRaw", kind: "inverse", value: "" },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G23", "G20"),
  },
  "Test du Labyrinthe (LT-G)": {
    label: L("Test du Labyrinthe (LT-G)", "Labyrinth-Test (NAI)"),
    params: [
      { name: "tempsRaw", label: L('Temps "', 'Zeit "'), type: "labyrinthTempsRaw", value: "" },
      { name: "erreursRaw", label: L("Erreurs", "Fehler"), type: "labyrinthErreursRaw", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G24", "G21"),
  },
  "Test de l'horloge": {
    label: L("Test de l'horloge", "Uhrentest"),
    params: [
      { name: "score", label: L("Score (X/10)", "Punktzahl (X/7)"), type: "scoreClock", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G25", "G22"),
    evalRule: "clockManos",
  },
  "Tâche des Apprentis": {
    label: L("Tâche des Apprentis", "Einsatzplan erstellen"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: L("", "Zeit= X, Fehler= X, Vorgehensweise systematisch/unsystematisch") }],
    type: "manual",
    resultLoc: loc("G26", "G23"),
  },
  "Test de Stroop, partie 4 (D-KEFS, 2001)": {
    label: L("Test de Stroop, partie 4 (D-KEFS, 2001)", "Stroop-Test, 4. Aufgabe (D-KEFS, 2001)"),
    params: [
      { name: "tempsNS", label: L('Temps "', 'Zeit "'), type: "stroopDkefsRaw", kind: "cond4Time", value: "" },
      { name: "fautesNS", label: L("Fautes total", "Fehler total"), type: "stroopDkefsRaw", kind: "cond4TotalScaled", value: "" },
      { name: "diffNS", label: L("différence temps 4.-3. NS", "Differenz Zeit 4.-3. WP"), type: "stroopDkefsRaw", kind: "cond43Diff", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G31", "G28"),
  },
  "Test de Stroop, partie 3 (D-KEFS, 2001)": {
    label: L("Test de Stroop, partie 3 (D-KEFS, 2001)", "Stroop-Test, 3. Aufgabe (D-KEFS, 2001)"),
    params: [
      { name: "tempsNS", label: L('Temps "', 'Zeit "'), type: "stroopDkefsRaw", kind: "cond3Time", value: "" },
      { name: "fautesNS", label: L("Fautes", "Fehler"), type: "stroopDkefsRaw", kind: "cond3TotalScaled", value: "" },
      { name: "diffNS", label: L("différence temps 3.-1. NS", "Differenz Zeit 3.-1. WP"), type: "stroopDkefsRaw", kind: "cond31Diff", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G32", "G29"),
  },
  "Victoria Stroop, partie B (2009)": {
    label: L("Victoria Stroop, partie B (2009)", "Victoria Stroop, Teil B (2009)"),
    params: [
      { name: "tempsPC", label: L('Temps Mots "', 'Zeit Wörter "'), type: "stroopVRaw", kind: "tMots", value: "" },
      { name: "erreursPC", label: L("Erreurs Mots (n)", "Fehler Wörter (n)"), type: "stroopVRaw", kind: "errMots", value: "" },
      { name: "interferenceFaiblePC", label: L("Indice interférence faible", "Index schwache Interferenz"), type: "stroopVRaw", kind: "ifMC", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G33", "G30"),
  },
  "Victoria Stroop, partie C (2009)": {
    label: L("Victoria Stroop, partie C (2009)", "Victoria Stroop, Teil C (2009)"),
    params: [
      { name: "tempsPC", label: L('Temps Interférence "', 'Zeit Interferenz "'), type: "stroopVRaw", kind: "tInterference", value: "" },
      { name: "erreursPC", label: L("Erreurs totales (n)", "Fehler total (n)"), type: "stroopVRaw", kind: "errInterfTotal", value: "" },
      { name: "interferenceFortePC", label: L("Indice d'interférence forte", "Index starke Interferenz"), type: "stroopVRaw", kind: "ifIC", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G34", "G31"),
  },
  "BREF": {
    label: L("BREF", "FAB"),
    params: [
      { name: "sim", label: L("Sim /3", "Ähnl. /3"), type: "input", denom: 3, prefix: L("Sim:", "Ähnl.:"), value: "" },
      { name: "fluence", label: L("Fluence /3", "Fluenz /3"), type: "input", denom: 3, prefix: L("Fluence:", "Fluenz:"), value: "" },
      { name: "sequMotrices", label: L("Sequ. motrices /3", "Mot. Sequenz /3"), type: "input", denom: 3, prefix: L("Sequ. motrices", "Mot. Sequenz"), value: "" },
      { name: "consContr", label: L("Cons. contr. /3", "Widerl. Instrukt. /3"), type: "input", denom: 3, prefix: L("Cons. contr.", "Widerl. Instrukt."), value: "" },
      { name: "goNogo", label: L("Go-Nogo /3", "Go-Nogo /3"), type: "input", denom: 3, prefix: L("Go-Nogo", "Go-Nogo"), value: "" },
      { name: "grasping", label: L("Grasping /3", "Grasping /3"), type: "input", denom: 3, prefix: L("Grasping", "Grasping"), value: "" },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G35", "G32"),
    evalRule: "brefTotal",
  },
  "Frises": {
    label: L("Frises", "Lurija-Schleife"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: L("X séquences réussies, XY", "X erfolgreiche Sequenzen, XY") }],
    type: "manual",
    resultLoc: loc("G36", "G33"),
  },
  "Orientation temporelle": {
    label: L("Orientation temporelle", "Zeitliche Orientierung"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G38", "G35"),
  },
  "Orientation spatiale": {
    label: L("Orientation spatiale", "Örtlich-geographische Orientierung"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G39", "G36"),
  },
  "Orientation personnelle": {
    label: L("Orientation personnelle", "Autopsychische Orientierung"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G40", "G37"),
  },
  "Orientation circonstancielle": {
    label: L("Orientation circonstancielle", "Situative Orientierung"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G41", "G38"),
  },
  "Mémoire des chiffres MDC (WAIS-IV)": {
    label: L("Mémoire des chiffres MDC (WAIS-IV)", "Zahlenspanne vorwärts (WAIS-IV)"),
    params: [
      { name: "empanDirectRaw", label: L("Empan direct", "Spanne vorwärts"), type: "empanRaw", kind: "emcd", value: "" },
      { name: "empanDirectStability", type: "stability", linkedTo: "empanDirectRaw", value: "" },
      { name: "mcdRaw", label: L("MCD brut", "MCD Rohwert"), type: "subtestRaw", kind: "mcd", nsLabel: L("MCD NS", "MCD WP"), value: "" },
    ],
    type: "manual",
    resultLoc: loc("G43", "G40"),
  },
  "Mémoire des chiffres MDC (WAIS-III)": {
    label: L("Mémoire des chiffres MDC (WAIS-III)", "Zahlenspanne vorwärts (WAIS-III)"),
    params: [
      { name: "empanDirectRaw", label: L("Empan direct", "Spanne vorwärts"), type: "empanRaw", kind: "emcd", value: "" },
      { name: "empanDirectStability", type: "stability", linkedTo: "empanDirectRaw", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G44", null),
  },
  "RL/RI-16 (Van der Linden)": {
    label: L("RL/RI-16 (Van der Linden)", "RL/RI-16 (Van der Linden)"),
    params: [
      { name: "RL1", label: L("RL1", "RL1"), type: "vdlRaw", value: "" },
      { name: "RL2", label: L("RL2", "RL2"), type: "vdlRaw", value: "" },
      { name: "RL3", label: L("RL3", "RL3"), type: "vdlRaw", value: "" },
      { name: "RLD", label: L("RLD", "RLD"), type: "vdlRaw", value: "" },
      { name: "RIM", label: L("RIM", "RIM"), type: "vdlRaw", value: "" },
      { name: "RT1", label: L("RT1", "RT1"), type: "vdlRaw", value: "" },
      { name: "RT2", label: L("RT2", "RT2"), type: "vdlRaw", value: "" },
      { name: "RT3", label: L("RT3", "RT3"), type: "vdlRaw", value: "" },
      { name: "RTD", label: L("RTD", "RTD"), type: "vdlRaw", value: "" },
      { name: "commentaire", label: L("comment", "Kommentar"), displayLabel: L("Reconnaissance verbale différée (liste de mots)", "Verbales Wiedererkennen (Wortliste)"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G48", "G44"),
    vdlResultLocs: {
      fr: { encodage: "G48", rappelImmediat: "G49", rappelLibre: "G50", rappelIndice: "G51", commentaire: "G52" },
      de: { encodage: "G44", rappelImmediat: "G45", rappelLibre: "G46", rappelIndice: "G47", commentaire: "G48" },
    },
  },
  "RL/RI-16 (Amieva, 2007)": {
    label: L("RL/RI-16 (Amieva, 2007)", "RL/RI-16 (Amieva, 2007)"),
    params: [
      { name: "rappelImmediat", label: L("Rappel immédiat", "Unmittelbarer Abruf"), type: "amievaRaw", value: "" },
      { name: "rappelLibre1", label: L("Rappel libre 1", "Freier Abruf 1"), type: "amievaRaw", value: "" },
      { name: "rappelLibre2", label: L("Rappel libre 2", "Freier Abruf 2"), type: "amievaRaw", value: "" },
      { name: "rappelLibre3", label: L("Rappel libre 3", "Freier Abruf 3"), type: "amievaRaw", value: "" },
      { name: "rappelLibreDiffere", label: L("Rappel libre différé", "Verzögerter freier Abruf"), type: "amievaRaw", value: "" },
      { name: "rappelIndice1", label: L("Rappel indicé 1", "Abruf mit Hinweis 1"), type: "amievaRaw", value: "" },
      { name: "rappelIndice2", label: L("Rappel indicé 2", "Abruf mit Hinweis 2"), type: "amievaRaw", value: "" },
      { name: "rappelIndice3", label: L("Rappel indicé 3", "Abruf mit Hinweis 3"), type: "amievaRaw", value: "" },
      { name: "rappelIndiceDiffere", label: L("Rappel indicé différé", "Verzögerter Abruf mit Hinweis"), type: "amievaRaw", value: "" },
      { name: "repetitions", label: L("Répétitions", "Wiederholungen"), type: "amievaRaw", value: "" },
      { name: "intrusions", label: L("Intrusions", "Intrusionen"), type: "amievaRaw", value: "" },
      { name: "commentaire", label: L("comment", "Kommentar"), displayLabel: L("Reconnaissance verbale différée (liste de mots)", "Verbales Wiedererkennen (Wortliste)"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G53", "G49"),
    amievaResultLocs: {
      fr: { encodage: "G53", rappelLibre: "G55", rappelTotal: "G56", commentaire: "G57" },
      de: { encodage: "G49", rappelLibre: "G51", rappelTotal: "G52", commentaire: "G53" },
    },
  },
  "GERIA-12 rappels libres": {
    label: L("GERIA-12 rappels libres", "GERIA-12 freier Abruf"),
    params: [
      { name: "rl1z", label: L("RL1 z", "RL1 z"), type: "input", value: "" },
      { name: "rl2z", label: L("RL2 z", "RL2 z"), type: "input", value: "" },
      { name: "repetitions", label: L("Répétitions", "Wiederholungen"), type: "input", value: "" },
      { name: "intrusions", label: L("Intrusions", "Intrusionen"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G58", "G54"),
    geria12Cells: { rl1z: "P5", rl2z: "Q5" },
  },
  "GERIA-12 rappels indicés": {
    label: L("GERIA-12 rappels indicés", "GERIA-12 Abruf mit Hinweis"),
    params: [
      { name: "rimPC", label: L("RIM PC", "RIM PR"), type: "input", value: "" },
      { name: "rt1PC", label: L("RT1 PC", "RT1 PR"), type: "input", value: "" },
      { name: "rt2PC", label: L("RT2 PC", "RT2 PR"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G59", "G55"),
    geria12Cells: { rimPC: "P9", rt1PC: "Q9", rt2PC: "R9" },
  },
  "GERIA-12 rappel différé libre": {
    label: L("GERIA-12 rappel différé libre", "GERIA-12 verzögerter freier Abruf"),
    params: [
      { name: "rdz", label: L("RD z", "RD z"), type: "input", value: "" },
      { name: "repetitions", label: L("Répétitions", "Wiederholungen"), type: "input", value: "" },
      { name: "intrusions", label: L("Intrusions", "Intrusionen"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G60", "G56"),
    geria12Cells: { rdz: "R5" },
  },
  "GERIA-12 rappel différé indicé": {
    label: L("GERIA-12 rappel différé indicé", "GERIA-12 verzögerter Abruf mit Hinweis"),
    params: [{ name: "rtdPC", label: L("RTD PC", "RTD PR"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G61", "G57"),
    geria12Cells: { rtdPC: "S9" },
  },
  "GERIA-12 reconnaissance différée": {
    label: L("GERIA-12 reconnaissance différée", "GERIA-12 verzögertes Wiedererkennen"),
    params: [
      { name: "recScore", label: L("Score /12", "Punktzahl /12"), type: "input", value: "" },
      { name: "recPC", label: L("PC", "PR"), type: "input", value: "" },
      { name: "recFRSemPC", label: L("0 FR / FR sémantique PC", "0 FR / semantische Fehler PR"), type: "input", value: "" },
      { name: "recFRPhonPC", label: L("FR phonologique PC", "phonologische Fehler PR"), type: "input", value: "" },
      { name: "deficit", label: L("Verdict", "Bewertung"), type: "deficitToggle", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G62", "G58"),
    geria12Cells: { recScore: "P12", recPC: "Q12", recFRSemPC: "P13", recFRPhonPC: "P14" },
  },
  // Single test per measure -- versionByAge picks the booklet (B+C for 16-64,
  // A+B for 65-90), and the active version label is shown in the test card.
  "Textgedächtnis unmittelbar (WMS-IV)": {
    label: L("Textgedächtnis unmittelbar (WMS-IV)", "Textgedächtnis unmittelbar (WMS-IV)"),
    params: [
      { name: "rawScore", label: L("Score brut (Gesamtergebnis)", "Rohwert (Gesamtergebnis)"), type: "input", value: "" },
    ],
    type: "parquet",
    calcType: "lgI",
    versionByAge: [
      { minAge: 16, maxAge: 64, version: "bc", label: L("Geschichten B & C (16-64 ans)", "Geschichten B & C (16-64 Jahre)") },
      { minAge: 65, maxAge: 90, version: "ab", label: L("Geschichten A & B (65-90 ans)", "Geschichten A & B (65-90 Jahre)") },
    ],
    resultLoc: loc(null, "G59"),
  },
  "Textgedächtnis mittelfristig (WMS-IV)": {
    label: L("Textgedächtnis mittelfristig (WMS-IV)", "Textgedächtnis mittelfristig (WMS-IV)"),
    params: [
      { name: "rawScore", label: L("Score brut (Gesamtergebnis)", "Rohwert (Gesamtergebnis)"), type: "input", value: "" },
    ],
    type: "parquet",
    calcType: "lgII",
    versionByAge: [
      { minAge: 16, maxAge: 64, version: "bc", label: L("Geschichten B & C (16-64 ans)", "Geschichten B & C (16-64 Jahre)") },
      { minAge: 65, maxAge: 90, version: "ab", label: L("Geschichten A & B (65-90 ans)", "Geschichten A & B (65-90 Jahre)") },
    ],
    resultLoc: loc(null, "G60"),
  },
  "Textinhalte wiedererkennen (WMS-IV)": {
    label: L("Textinhalte wiedererkennen (WMS-IV)", "Textinhalte wiedererkennen (WMS-IV)"),
    params: [
      { name: "rawScore", label: L("Score brut (Gesamtrohwert)", "Rohwert (Gesamtrohwert)"), type: "input", value: "" },
    ],
    type: "parquet",
    calcType: "lgIIRecog",
    versionByAge: [
      { minAge: 16, maxAge: 64, version: "bc", label: L("Geschichten B & C (16-64 ans)", "Geschichten B & C (16-64 Jahre)") },
      { minAge: 65, maxAge: 90, version: "ab", label: L("Geschichten A & B (65-90 ans)", "Geschichten A & B (65-90 Jahre)") },
    ],
    resultLoc: loc(null, "G61"),
  },
  "5 mots (Dubois)": {
    label: L("5 mots (Dubois)", "5 Wörter (Dubois)"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G63", null),
  },
  "Bildertest (NAI)": {
    label: L("Bildertest (NAI)", "Bildertest (NAI)"),
    params: [
      { name: "setting", label: L("Indépendant/EMS", "Selbstständig/Heim"), type: "bildertestSettingToggle", value: "haushalt" },
      { name: "wert", label: L("Score brut (0-7)", "Rohwert (0-7)"), type: "bildertestWertRaw", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G64", null),
  },
  "Récit BEM-144 rappel immédiat": {
    label: L("Récit BEM-144 rappel immédiat", "BEM-144 Erzählung, unmittelbarer Abruf"),
    params: [{ name: "rappelImmediatPC", label: L("Rappel immédiat PC", "Unmittelbarer Abruf PR"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G65", null),
  },
  "Récit BEM-144 rappel différé": {
    label: L("Récit BEM-144 rappel différé", "BEM-144 Erzählung, verzögerter Abruf"),
    params: [{ name: "rappelDifferePC", label: L("Rappel différé PC", "Verzögerter Abruf PR"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G66", null),
  },
  "Récit PeCPA-2r immédiat": {
    label: L("Récit PeCPA-2r immédiat", "Récit PeCPA-2r unmittelbar"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: L("Rappel immédiat libre X/3 (X éléments rapportés); Rappel immédiat indicé X/7", "Unmittelbarer freier Abruf X/3 (X Elemente wiedergegeben); Unmittelbarer Abruf mit Hinweisen X/7") }],
    type: "manual",
    resultLoc: loc("G67", "G62"),
  },
  "Récit PeCPA-2r différé": {
    label: L("Récit PeCPA-2r différé", "Récit PeCPA-2r verzögert"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: L("Rappel différé libre X/3 (X éléments rapportés); Rappel différé indicé X/7", "Verzögerter freier Abruf X/3 (X Elemente wiedergegeben); Verzögerter Abruf mit Hinweisen X/7") }],
    type: "manual",
    resultLoc: loc("G68", "G63"),
  },
  "Empan direct (WMS-R)": {
    label: L("Empan direct (WMS-R)", "Blockspanne vorwärts (WMS-R)"),
    params: [
      { name: "empanRaw", label: L("Score d'empan", "Spannenwert"), type: "input", value: "" },
      { name: "empanStability", type: "stability", linkedTo: "empanRaw", value: "" },
      { name: "scoreGlobal", label: L("Score global", "Gesamtrohwert"), type: "wmsrBlockspanRaw", kind: "direct", value: "" },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G70", "G65"),
  },
  "RCFT mémoire immédiat (Rey)": {
    label: L("RCFT mémoire immédiat (Rey)", "RCFT unmittelbarer Abruf (Rey)"),
    params: [
      { name: "immediateRaw", label: L("Rappel immédiat (brut, /36)", "Unmittelbarer Abruf (Rohwert, /36)"), type: "input", value: "" },
    ],
    type: "parquet",
    calcType: "rcftImmediat",
    resultLoc: loc("G71", "G66"),
  },
  "RCFT mémoire différé (Rey)": {
    label: L("RCFT mémoire différé (Rey)", "RCFT verzögerter Abruf (Rey)"),
    params: [
      { name: "delayedRaw", label: L("Rappel différé (brut, /36)", "Verzögerter Abruf (Rohwert, /36)"), type: "input", value: "" },
    ],
    type: "parquet",
    calcType: "rcftDiffere",
    resultLoc: loc("G72", "G67"),
  },
  "Figure de Rey simplifiée (rappel)": {
    label: L("Figure de Rey simplifiée (rappel)", "Abruf der vereinfachten Rey-Figur"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc(null, "G68"),
  },
  "Portes (Doors and People)": {
    label: L("Portes (Doors and People)", "Doors and People Test"),
    params: [
      { name: "partieARaw", label: L("Partie A (/12)", "Teil A (/12)"), type: "portesRaw", kind: "A", value: "" },
      { name: "partieBRaw", label: L("Partie B (/12)", "Teil B (/12)"), type: "portesRaw", kind: "B", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G75", null),
  },
  "5 dessins (Croisile)": {
    label: L("5 dessins (Croisile)", "5 Zeichnungen (Croisile)"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G76", "G71"),
  },
  "Bissection de lignes (Ferber)": {
    label: L("Bissection de lignes (Ferber)", "Linienhalbieren (Ferber)"),
    params: [
      { name: "deviation", label: L("Déviation moy. (%)", "Mittlere Abweichung (%)"), type: "input", value: "" },
      { name: "direction", label: L("Direction", "Richtung"), type: "directionToggle", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G82", "G77"),
    evalRule: "bissectionFerber",
  },
  "Apples Test (Humphreys)": {
    label: L("Apples Test (Humphreys)", "Apples Test (Humphreys)"),
    params: [
      { name: "cibles", label: L("Cibles détectées (/50)", "Erkannte Ziele (/50)"), type: "input", value: "" },
      { name: "diffGD", label: L("Diff. omissions G-D", "Diff. Auslassungen L-R"), type: "input", value: "" },
      { name: "asymetrie", label: L("Asymétrie détection incomplètes", "Asymmetrie Erkennung inkomplett"), type: "input", value: "" },
      {
        name: "commentaire",
        label: L("comment", "Kommentar"),
        type: "input",
        value: L(
          "X omissions G, X omissions D, Durée'', Début à G/D, exploration systématique/peu stratégique, X erreurs (X pommes incomplètes à G, X pommes incomplètes à D)",
          "X Auslassungen L, X Auslassungen R, Dauer'', Beginn L/R, systematische/wenig strategische Exploration, X Fehler (X unvollständige Äpfel L, X unvollständige Äpfel R)"
        ),
      },
    ],
    type: "manual",
    evalRule: "applesHumphreys",
    resultLoc: loc("G83", "G78"),
  },
  "Bells Test (Rousseaux)": {
    label: L("Bells Test (Rousseaux)", "Bells Test (Rousseaux)"),
    params: [
      { name: "omG", label: L("Omissions G", "Auslassungen L"), type: "bellsRaw", kind: "omG", value: "" },
      { name: "omD", label: L("Omissions D", "Auslassungen R"), type: "bellsRaw", kind: "omD", value: "" },
      { name: "temps", label: L('Temps "', 'Zeit "'), type: "bellsRaw", kind: "temps", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G84", "G79"),
    evalRule: "bellsRousseaux",
  },
  "RCFT copie (Rey)": {
    label: L("RCFT copie (Rey)", "RCFT Kopie (Rey)"),
    params: [
      { name: "copyRaw", label: L("Copie (brut, /36)", "Kopie (Rohwert, /36)"), type: "input", value: "" },
      { name: "timeRaw", label: L('Temps "', 'Zeit "'), type: "input", value: "" },
    ],
    type: "parquet",
    calcType: "rcftCopie",
    resultLoc: loc("G85", "G80"),
  },
  "Figure de Rey simplifiée (copie)": {
    label: L("Figure de Rey simplifiée (copie)", "Kopie der vereinfachten Rey-Figur"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc(null, "G82"),
  },
  "VOSP": {
    label: L("VOSP", "VOSP"),
    params: [
      { name: "screening", label: L("Screening /20", "Screening /20"), type: "input", value: "" },
      { name: "lettresFragmentees", label: L("Lettres fragmentées /20", "Fragmentierte Buchstaben /20"), type: "input", value: "" },
      { name: "denombrementPoints", label: L("Dénombrement de points /10", "Punkte zählen /10"), type: "input", value: "" },
      { name: "discriminationPosition", label: L("Discrimination de la position /20", "Positionen unterscheiden /20"), type: "input", value: "" },
      { name: "localisationChiffres", label: L("Localisation de chiffres /10", "Lokalisation von Zahlen /10"), type: "input", value: "" },
      { name: "analysesCubes", label: L("Analyses de cubes /10", "Würfel-Analyse /10"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G87", "G83"),
    evalRule: "vosp",
  },
  "Poppelreuter": {
    label: L("Poppelreuter", "Poppelreuter"),
    params: [
      {
        name: "commentaire",
        label: L("comment", "Kommentar"),
        type: "input",
        value: L("Früchte /5, Kleidungsstücke /4", "Früchte /5, Kleidungsstücke /4"),
      },
    ],
    type: "manual",
    resultLoc: loc(null, "G84"),
  },
  "Maxi-Blatt": {
    label: L("Maxi-Blatt", "Maxi-Blatt"),
    params: [
      {
        name: "commentaire",
        label: L("comment", "Kommentar"),
        type: "input",
        value: L(
          "Maskierte Wörter /2, Kanizsa Figuren  /2, Stilisierte Uhren  /3, Maskierte Tiere  /3",
          "Maskierte Wörter /2, Kanizsa Figuren  /2, Stilisierte Uhren  /3, Maskierte Tiere  /3"
        ),
      },
    ],
    type: "manual",
    resultLoc: loc(null, "G85"),
  },
  "Bildbeschreibung (Schneeball/Blinde Kuh)": {
    label: L("Bildbeschreibung (Schneeball/Blinde Kuh)", "Bildbeschreibung"),
    params: [
      {
        name: "commentaire",
        label: L("comment", "Kommentar"),
        type: "input",
        value: L("Schneeball: \nBlinde Kuh: ", "Schneeball: \nBlinde Kuh: "),
      },
    ],
    type: "manual",
    resultLoc: loc(null, "G86"),
  },
  "Appariement formes géométriques (TGV)": {
    label: L("Appariement formes géométriques (TGV)", "Formenzuordnung (TGV)"),
    params: [
      { name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", scoreMax: 12 },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G88", null),
    evalRule: "tgvScore",
  },
  "Figures superposées": {
    label: L("Figures superposées", "Überlagerte Figuren"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G89", null),
  },
  "Identification de dessins au trait": {
    label: L("Identification de dessins au trait", "Identifikation von Strichzeichnungen"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G90", null),
  },
  "Reconnaissance de visages célèbres": {
    label: L("Reconnaissance de visages célèbres", "Wiedererkennen prominenter Gesichter"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G91", null),
  },
  "Description d'images": {
    label: L("Description d'images", "Bildbeschreibung"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G92", null),
  },
  "Description de l'image de la gare": {
    label: L("Description de l'image de la gare", "Beschreibung des Bahnhofsbildes"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G93", null),
  },
  // Mahieux-Laurent (2009) and Macoir (2025) are the same 3 praxie subtests with
  // different norms. Merged into one test each: the norm applied is chosen by age
  // (≤49 → Mahieux-Laurent scoreCutoff; 50-89 → Macoir BBEP age-band cutoff). Both
  // norms share the same scoresheet row (the Mahieux rows, which have DE counterparts),
  // so only one result shows. See ageNorms resolution in TestCard.
  "Production de gestes symboliques": {
    label: L("Production de gestes symboliques", "Symbolische Gesten"),
    params: [
      { name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", scoreMax: 5 },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G95", "G89"),
    ageNorms: [
      { maxAge: 49, evalRule: "scoreCutoff", evalCutoff: 4, label: L("Mahieux-Laurent et al., 2009", "Mahieux-Laurent et al., 2009") },
      { evalRule: "bbepMacoir2025", bbepSubtest: "symbolic", label: L("Macoir et al., 2025", "Macoir et al., 2025") },
    ],
    maxAgeYears: 89,
  },
  "Production de pantomimes d'utilisation": {
    label: L("Production de pantomimes d'utilisation", "Pantomime der Verwendung"),
    params: [
      { name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", scoreMax: 10 },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G96", "G90"),
    ageNorms: [
      { maxAge: 49, evalRule: "scoreCutoff", evalCutoff: 8, label: L("Mahieux-Laurent et al., 2009", "Mahieux-Laurent et al., 2009") },
      { evalRule: "bbepMacoir2025", bbepSubtest: "pantomimes", label: L("Macoir et al., 2025", "Macoir et al., 2025") },
    ],
    maxAgeYears: 89,
  },
  "Imitation de gestes sans signification": {
    label: L("Imitation de gestes sans signification", "Imitation bedeutungsloser Gesten"),
    params: [
      { name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", scoreMax: 8 },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G97", "G91"),
    ageNorms: [
      { maxAge: 49, evalRule: "scoreCutoff", evalCutoff: 6, label: L("Mahieux-Laurent et al., 2009", "Mahieux-Laurent et al., 2009") },
      { evalRule: "bbepMacoir2025", bbepSubtest: "meaningless", label: L("Macoir et al., 2025", "Macoir et al., 2025") },
    ],
    maxAgeYears: 89,
  },
  "Parole": {
    label: L("Parole", "Aussprache"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G102", "G96"),
  },
  "Discours spontané": {
    label: L("Discours spontané", "Spontansprache"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G103", "G97"),
  },
  "Compréhension orale": {
    label: L("Compréhension orale", "Sprachverständnis"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G104", "G98"),
  },
  "Test de Stroop, partie 2 (D-KEFS, 2001)": {
    label: L("Test de Stroop, partie 2 (D-KEFS, 2001)", "Stroop-Test, 2. Aufgabe (D-KEFS, 2001)"),
    params: [
      { name: "tempsNS", label: L('Temps "', 'Zeit "'), type: "stroopDkefsRaw", kind: "cond2Time", value: "" },
      { name: "erreursPC", label: L("Erreurs", "Fehler"), type: "stroopDkefsRaw", kind: "cond2Errors", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G105", "G99"),
  },
  "Test de Stroop, partie 1 (D-KEFS, 2001)": {
    label: L("Test de Stroop, partie 1 (D-KEFS, 2001)", "Stroop-Test, 1. Aufgabe (D-KEFS, 2001)"),
    params: [
      { name: "tempsNS", label: L('Temps "', 'Zeit "'), type: "stroopDkefsRaw", kind: "cond1Time", value: "" },
      { name: "erreursPC", label: L("Erreurs", "Fehler"), type: "stroopDkefsRaw", kind: "cond1Errors", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G106", "G100"),
  },
  "Victoria Stroop, partie A (2009)": {
    label: L("Victoria Stroop, partie A (2009)", "Victoria Stroop, Teil A (2009)"),
    params: [
      { name: "tempsPC", label: L('Temps Couleurs "', 'Zeit Farben "'), type: "stroopVRaw", kind: "tCouleurs", value: "" },
      { name: "erreurPC", label: L("Erreur Couleurs (n)", "Fehler Farben (n)"), type: "stroopVRaw", kind: "errCouleurs", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G107", "G101"),
  },
  "Dénomination de visages célèbres": {
    label: L("Dénomination de visages célèbres", "Benennen prominenter Gesichter"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G109", null),
  },
  "DTLA (2017)": {
    label: L("DTLA (2017)", "DTLA (2017)"),
    params: [
      { name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", scoreMax: 100 },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G110", null),
    evalRule: "dtla2017",
  },
  "Lecture à haute voix": {
    label: L("Lecture à haute voix", "Lautes Lesen"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G111", null),
  },
  "Écriture automatique (nom, prénom, adresse)": {
    label: L("Écriture automatique (nom, prénom, adresse)", "Automatisches Schreiben (Name, Vorname, Adresse)"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G112", null),
  },
  "Écriture spontanée": {
    label: L("Écriture spontanée", "Spontanes Schreiben"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G113", "G107"),
  },
  "Screening du calcul": {
    label: L("Screening du calcul", "Rechen-Screening"),
    params: [{ name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G115", "G110"),
  },
  "MoCA": {
    label: L("MoCA", "MoCA"),
    params: [
      { name: "score", label: L("Score MoCA", "MoCA-Punktzahl"), type: "input", value: "", scoreMax: 30 },
      { name: "commentaire", label: L("comment", "Kommentar"), type: "input", value: "" },
    ],
    type: "manual",
    resultLoc: loc("G118", "G117"),
    evalRule: "mocaTotal",
  },
  "DIA-S (Heidenblut & Zank, 2010)": {
    label: L("DIA-S (Heidenblut & Zank, 2010)", "DIA-S (Heidenblut & Zank, 2010)"),
    params: [{
      name: "score",
      label: L("Score", "Punktzahl"),
      type: "input",
      value: "",
      scoreMax: 10,
      scoreLevels: [
        { lt: 3, label: L("non significatif", "unauffällig") },
        { lte: 3, label: L("suspicion de symptomatologie dépressive", "Vd.a. depressive Symptomatik") },
        { label: L("symptomatologie dépressive cliniquement pertinente", "klinisch relevante depressive Symptomatik") },
      ],
    }],
    type: "manual",
    resultLoc: loc(null, "G120"),
    evalRule: "diaS",
  },
  "Symptomatologie dépressive (Mini-GDS)": {
    label: L("Symptomatologie dépressive (Mini-GDS)", "Depression (Mini-GDS)"),
    params: [{ name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", significatifThreshold: 1, scoreMax: 4 }],
    type: "manual",
    resultLoc: loc("G121", "G121"),
  },
  "Symptomatologie anxieuse (GAI-FC-SF)": {
    label: L("Symptomatologie anxieuse (GAI-FC-SF)", "Angst (GAI-FC-SF)"),
    params: [{ name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", significatifThreshold: 3, scoreMax: 5 }],
    type: "manual",
    resultLoc: loc("G122", "G122"),
  },
  "HADS-D (Herrmann et al., 2011)": {
    label: L("HADS-D (Herrmann et al., 2011)", "HADS-D (Herrmann et al., 2011)"),
    params: [
      {
        name: "angst",
        label: L("Angstwert", "Angstwert"),
        type: "input",
        value: "",
        scoreMax: 21,
        scoreLevels: [
          { lt: 8, label: L("non significatif", "nicht signifikant") },
          { lt: 10, label: L("limite", "grenzwertig") },
          { label: L("significatif", "signifikant") },
        ],
      },
      {
        name: "depression",
        label: L("Depressionswert", "Depressionswert"),
        type: "input",
        value: "",
        scoreMax: 21,
        scoreLevels: [
          { lt: 8, label: L("non significatif", "nicht signifikant") },
          { lt: 10, label: L("limite", "grenzwertig") },
          { label: L("significatif", "signifikant") },
        ],
      },
    ],
    type: "manual",
    resultLoc: loc(null, "G123"),
    evalRule: "hadsD",
  },
  "Symptomatologie anxieuse (GAD-7)": {
    label: L("Symptomatologie anxieuse (GAD-7)", "Angst (GAD-7)"),
    params: [{ name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", significatifThreshold: 8, scoreMax: 21 }],
    type: "manual",
    resultLoc: loc("G123", null),
  },
  "Symptomatologie dépressive (NDDI-E)": {
    label: L("Symptomatologie dépressive (NDDI-E)", "Depression (NDDI-E)"),
    params: [{ name: "score", label: L("Score", "Punktzahl"), type: "input", value: "", significatifThreshold: 16, scoreMax: 24 }],
    type: "manual",
    resultLoc: loc("G124", null),
  },
  "Somnolence (Epworth)": {
    label: L("Somnolence (Epworth)", "Tagesschläfrigkeit (Epworth)"),
    params: [{
      name: "score",
      label: L("Score", "Punktzahl"),
      type: "input",
      value: "",
      scoreMax: 24,
      scoreLevels: [
        { lt: 9, label: L("non significatif", "nicht signifikant") },
        { lt: 15, label: L("score indicateur d'un déficit de sommeil", "Hinweis auf Schlafdefizit") },
        { label: L("score évocateur de somnolence diurne excessive", "Hinweis auf erhöhte Tagesschläfrigkeit") },
      ],
    }],
    type: "manual",
    resultLoc: loc("G125", "G124"),
  },
  "Autonomie en vie quotidienne (IADL)": {
    label: L("Autonomie en vie quotidienne (IADL)", "Alltagsautonomie (IADL)"),
    params: [{ name: "note", label: L("comment", "Kommentar"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G126", null),
  },
  "NOSGER (Spiegel et al., 1999)": {
    label: L("NOSGER (Spiegel et al., 1999)", "NOSGER (Spiegel et al., 1999)"),
    params: [],
    type: "nosger",
    resultLoc: loc(null, "G127"),
    evalRule: "nosger",
  },
  "BDQ (Rascovsky et al., 2011)": {
    label: L("BDQ (Rascovsky et al., 2011)", "BDQ (Rascovsky et al., 2011)"),
    params: [],
    type: "bdq",
    resultLoc: loc(null, "G129"),
    evalRule: "bdq",
  },
  "Fonctionnement cognitif global (IQCODE)": {
    label: L("Fonctionnement cognitif global (IQCODE)", "Globale kognitive Leistungsfähigkeit (IQCODE)"),
    params: [{
      name: "score",
      label: L("Score", "Punktzahl"),
      type: "input",
      value: "",
      scoreLevels: [
        { lte: 3.25, label: L("pas d'indices de détérioration", "Keine Hinweise") },
        { label: L("indices de détérioration", "Hinweise") },
      ],
    }],
    type: "manual",
    resultLoc: loc("G127", "G125"),
  },
  "Autonomie en vie quotidienne (CDR)": {
    label: L("Autonomie en vie quotidienne (CDR)", "Alltagsautonomie (CDR)"),
    params: [{ name: "score", label: L("Score", "Punktzahl"), type: "input", value: "" }],
    type: "manual",
    resultLoc: loc("G128", "G128"),
  },
};

// Helpers exposed for components/utils that need language-aware fields.
// `loc` is a {fr, de} object, or (legacy) a plain string.
export const resolveLoc = (locField, lang) => {
  if (!locField) return undefined;
  if (typeof locField === "string") return locField;
  return locField[lang] ?? locField.fr ?? null;
};

export const resolveLabel = (labelField, lang) => {
  if (labelField == null) return labelField;
  if (typeof labelField === "string") return labelField;
  if (typeof labelField === "object" && (labelField.fr !== undefined || labelField.de !== undefined)) {
    return labelField[lang] ?? labelField.fr ?? "";
  }
  return labelField;
};

// Pick the display name for a test key. Falls back to the FR key if no label.
export const getTestLabel = (testKey, testEntry, lang) => {
  if (testEntry && testEntry.label) return resolveLabel(testEntry.label, lang);
  return testKey;
};

// Whether a test has a target row in the given language's scoresheet template.
// `resultLoc.de === null` marks tests absent from the DE scoresheet (see the
// `loc(fr, de)` definitions above). Unlike resolveLoc, this does NOT fall back
// to the FR cell, so de:null reads as "not available". Tests without a resultLoc
// (e.g. CERAD, type "xls") and legacy string locs are treated as available.
export const isTestAvailable = (testEntry, lang) => {
  if (!testEntry) return false;
  const locField = testEntry.resultLoc;
  if (!locField || typeof locField === "string") return true;
  return locField[lang] != null;
};

// Granular CERAD-Plus test keys that replaced the monolithic "CERAD" entry.
// Used both to build the default profiles below and to expand legacy saved
// profiles that still reference the old single "CERAD" key.
const CERAD_TEST_KEYS = [
  "TMT A (CERAD, 2005 / GREFEX, 2008)",
  "TMT B (CERAD, 2005 / GREFEX, 2008)",
  "Fluences Animaux (CERAD, 2005)",
  "Fluences Mots M (CERAD, 2005)",
  "10 mots (CERAD, 2005)",
  "10 mots (CERAD, 2005) (Row 48)",
  "10 mots (CERAD, 2005) (Row 49)",
  "Copie de figures (CERAD, 2005)",
  "Rappel de figures (CERAD, 2005)",
  "Boston Naming Test (CERAD, 2005)",
  "MMSE (CERAD, 2005)",
];

const defaultProfiles = [
  { name: "Jeune", lang: "fr", tests: ["TAP-Alerte", "TAP-GoNogo", "TAP-AttentionDivisée", "TAP-ChampVisuel", "TAP-Négligence", "TAP-BalayageVisuel", "TAP-M-Alerte", "TAP-M-GoNogo", "TAP-M-Distractibilité", "TAP-M-AttentionDivisée", "TAP-M-BalayageVisuel", "Color Trail 1", "Color Trail 2", "Code (WAIS-IV)"] },
  { name: "Adulte", lang: "fr", tests: ["TAP-Alerte", "TAP-GoNogo", "TAP-AttentionDivisée", "TAP-ChampVisuel", "TAP-Négligence", "TAP-BalayageVisuel", "TAP-M-Alerte", "TAP-M-GoNogo", "TAP-M-Distractibilité", "TAP-M-AttentionDivisée", "TAP-M-BalayageVisuel", "Color Trail 1", "Color Trail 2", "Code (WAIS-IV)", ...CERAD_TEST_KEYS] },
  { name: "Senior", lang: "fr", tests: ["TAP-Alerte", "Code (WAIS-IV)", ...CERAD_TEST_KEYS] },
];

const defaultPatientInfo = {
  patientId: "",
  nom: "",
  prenom: "",
  dateNaissance: "",
  dateExamen: todayISO(),
  dateExamenComparatif1: "",
  dateExamenComparatif2: "",
  lateralite: "",
  genre: "",
  niveau: "",
  langueMaternelle: "",
  anneesEtudes: "",
};

const PROFILE_RENAMES = {
  "RL/RI-16 commentaire (Van der Linden)": "RL/RI-16 (Van der Linden)",
  "RL/RI-16 encodage (Van der Linden)": "RL/RI-16 (Van der Linden)",
  "RL/RI-16 rappel immédiat (Van der Linden)": "RL/RI-16 (Van der Linden)",
  "RL/RI-16 rappel libre (Van der Linden)": "RL/RI-16 (Van der Linden)",
  "RL/RI-16 rappel indicé (Van der Linden)": "RL/RI-16 (Van der Linden)",
  "RL/RI-16 reconnaissance verbale différée (Van der Linden)": "RL/RI-16 (Van der Linden)",
  "RL/RI-16 encodage (Amieva)": "RL/RI-16 (Amieva, 2007)",
  "RL/RI-16 rappel libre (Amieva)": "RL/RI-16 (Amieva, 2007)",
  "RL/RI-16 rappel total (Amieva)": "RL/RI-16 (Amieva, 2007)",
  "RL/RI-16 commentaire (Amieva)": "RL/RI-16 (Amieva, 2007)",
  "Production de gestes symboliques (Mahieux-Laurent et al., 2009)": "Production de gestes symboliques",
  "Production de pantomimes d'utilisation (Mahieux-Laurent et al., 2009)": "Production de pantomimes d'utilisation",
  "Imitation de gestes sans signification (Mahieux-Laurent et al., 2009)": "Imitation de gestes sans signification",
  "Production de gestes symboliques (Macoir et al., 2025)": "Production de gestes symboliques",
  "Production de pantomimes d'utilisation (Macoir et al., 2025)": "Production de pantomimes d'utilisation",
  "Imitation de gestes sans signification (Macoir et al., 2025)": "Imitation de gestes sans signification",
  "Textgedächtnis unmittelbar (WMS-IV, A & B)": "Textgedächtnis unmittelbar (WMS-IV)",
  "Textgedächtnis mittelfristig (WMS-IV, A & B)": "Textgedächtnis mittelfristig (WMS-IV)",
  "Textinhalte wiedererkennen (WMS-IV, A & B)": "Textinhalte wiedererkennen (WMS-IV)",
  "Textgedächtnis unmittelbar (WMS-IV, B & C)": "Textgedächtnis unmittelbar (WMS-IV)",
  "Textgedächtnis mittelfristig (WMS-IV, B & C)": "Textgedächtnis mittelfristig (WMS-IV)",
  "Textinhalte wiedererkennen (WMS-IV, B & C)": "Textinhalte wiedererkennen (WMS-IV)",
};

// Tests that remain defined in defaultTests (for backward data compatibility)
// but are intentionally hidden from selection and stripped from saved profiles.
const HIDDEN_TEST_KEYS = new Set(["TAP-M-ChampVisuelActif"]);

function loadProfiles() {
  const saved = localStorage.getItem("profiles");
  if (!saved) return defaultProfiles;
  let parsed;
  try { parsed = JSON.parse(saved); } catch { return defaultProfiles; }
  const validTestNames = new Set(Object.keys(defaultTests));
  return parsed.map((p) => {
    const expanded = (p.tests || []).flatMap((t) => (t === "CERAD" ? CERAD_TEST_KEYS : [t]));
    const remapped = expanded.map((t) => PROFILE_RENAMES[t] || t);
    const deduped = Array.from(new Set(remapped));
    return {
      ...p,
      lang: p.lang || "fr",
      tests: deduped.filter((t) => validTestNames.has(t) && !HIDDEN_TEST_KEYS.has(t)),
    };
  });
}

function loadPatientInfo() {
  const saved = localStorage.getItem("patientInfo");
  if (!saved) return defaultPatientInfo;
  try { return { ...defaultPatientInfo, ...JSON.parse(saved) }; }
  catch { return defaultPatientInfo; }
}

// Tiny external store with subscribe/getState/setState. Used with
// useSyncExternalStore so consumers can subscribe to a selected slice
// of the state and re-render only when that slice's identity changes.
function createStore(initial) {
  let state = initial;
  const listeners = new Set();
  return {
    getState: () => state,
    setState: (updater) => {
      const next = typeof updater === "function" ? updater(state) : updater;
      if (next === state) return;
      state = next;
      listeners.forEach((l) => l());
    },
    subscribe: (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

export function TestDataProvider({ children }) {
  const [{ store, actions }] = useState(() => {
    const s = createStore({
      testData: defaultTests,
      patientInfo: loadPatientInfo(),
      profiles: loadProfiles(),
      selectedProfile: null,
    });
    const a = {
      updateTest: (testName, data) =>
        s.setState((cur) => ({
          ...cur,
          testData: { ...cur.testData, [testName]: { ...cur.testData[testName], ...data } },
        })),
      resetTestData: () => s.setState((cur) => ({ ...cur, testData: defaultTests })),
      resetTest: (testName) =>
        s.setState((cur) => ({
          ...cur,
          testData: { ...cur.testData, [testName]: defaultTests[testName] },
        })),
      updatePatientInfo: (field, value) =>
        s.setState((cur) => ({ ...cur, patientInfo: { ...cur.patientInfo, [field]: value } })),
      resetPatientInfo: () => {
        s.setState((cur) => ({ ...cur, patientInfo: defaultPatientInfo }));
        localStorage.removeItem("patientInfo");
      },
      setSelectedProfile: (profile) =>
        s.setState((cur) => ({ ...cur, selectedProfile: profile })),
      addProfile: (profile) =>
        s.setState((cur) => ({ ...cur, profiles: [...cur.profiles, profile] })),
      updateProfile: (index, profile) =>
        s.setState((cur) => ({
          ...cur,
          profiles: cur.profiles.map((p, i) => (i === index ? profile : p)),
        })),
      deleteProfile: (index) =>
        s.setState((cur) => ({
          ...cur,
          profiles: cur.profiles.filter((_, i) => i !== index),
        })),
    };
    return { store: s, actions: a };
  });

  // Persist profiles + patientInfo when their references change.
  useEffect(() => {
    let prevProfiles = store.getState().profiles;
    let prevPatientInfo = store.getState().patientInfo;
    return store.subscribe(() => {
      const cur = store.getState();
      if (cur.profiles !== prevProfiles) {
        prevProfiles = cur.profiles;
        localStorage.setItem("profiles", JSON.stringify(cur.profiles));
      }
      if (cur.patientInfo !== prevPatientInfo) {
        prevPatientInfo = cur.patientInfo;
        localStorage.setItem("patientInfo", JSON.stringify(cur.patientInfo));
      }
    });
  }, [store]);

  // Keep selectedProfile in sync when its underlying profile entry is edited.
  useEffect(() => {
    return store.subscribe(() => {
      const cur = store.getState();
      if (!cur.selectedProfile) return;
      const updated = cur.profiles.find((p) => p.name === cur.selectedProfile.name);
      if (updated && updated !== cur.selectedProfile) {
        store.setState((s) => ({ ...s, selectedProfile: updated }));
      }
    });
  }, [store]);

  // ctxValue identity is stable for the lifetime of the provider.
  const ctxRef = useRef(null);
  if (!ctxRef.current) ctxRef.current = { store, actions };

  return (
    <TestDataContext.Provider value={ctxRef.current}>
      {children}
    </TestDataContext.Provider>
  );
}

function useStoreCtx() {
  const ctx = useContext(TestDataContext);
  if (!ctx) throw new Error("useTestData must be used within TestDataProvider");
  return ctx;
}

// Subscribe to a selector. Re-renders only when the selector's return identity
// changes (Object.is comparison done by useSyncExternalStore).
export function useTestSelector(selector) {
  const { store } = useStoreCtx();
  return useSyncExternalStore(store.subscribe, () => selector(store.getState()));
}

// Stable references — never trigger re-renders by themselves.
export function useTestActions() {
  return useStoreCtx().actions;
}

// Returns a stable accessor for reading the latest state inside handlers
// without subscribing.
export function useTestSnapshot() {
  return useStoreCtx().store.getState;
}

// Slice hooks — each re-renders only when its slice's reference changes.
export function useTestSlice(name) {
  return useTestSelector((s) => s.testData[name]);
}
export function usePatientInfo() {
  return useTestSelector((s) => s.patientInfo);
}
export function useProfiles() {
  return useTestSelector((s) => s.profiles);
}
export function useSelectedProfile() {
  return useTestSelector((s) => s.selectedProfile);
}

// Legacy shim — subscribes to every slice and merges in actions. Existing
// callers keep working; new code should prefer the fine-grained hooks above.
export function useTestData() {
  const { actions } = useStoreCtx();
  const testData = useTestSelector((s) => s.testData);
  const patientInfo = useTestSelector((s) => s.patientInfo);
  const profiles = useTestSelector((s) => s.profiles);
  const selectedProfile = useTestSelector((s) => s.selectedProfile);
  return { testData, patientInfo, profiles, selectedProfile, ...actions };
}
